import {
  Skeleton,
  Box,
  Pagination,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useState, useMemo, useEffect } from "react";
import StoreProductCard from "./StoreProductCard";
import StoreEmptyState from "./StoreEmptyState";

export default function StoreProductsGrid({ products, loading }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [page, setPage] = useState(1);

  const productsPerPage = 3;

  useEffect(() => {
    setPage(1);
  }, [products, isMobile]);

  const paginatedProducts = useMemo(() => {
    if (!isMobile) return products;

    const start = (page - 1) * productsPerPage;
    const end = start + productsPerPage;

    return products.slice(start, end);
  }, [products, page, isMobile]);

  const totalPages = useMemo(() => {
    if (!isMobile) return 1;
    return Math.ceil(products.length / productsPerPage);
  }, [products.length, isMobile]);

  const handleChangePage = (_, value) => {
    setPage(value);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  if (loading) {
    return (
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(3, minmax(0, 1fr))",
            },
            gap: { xs: 2, md: 2.2 },
            alignItems: "stretch",
          }}
        >
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <Box
              key={item}
              sx={{
                width: "100%",
                minWidth: 0,
              }}
            >
              <Skeleton
                variant="rounded"
                animation="wave"
                sx={{
                  width: "100%",
                  height: { xs: 390, sm: 430, md: 440 },
                  borderRadius: { xs: "20px", md: "24px" },
                  transform: "none",
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  if (!products.length) {
    return <StoreEmptyState />;
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(3, minmax(0, 1fr))",
          },
          gap: { xs: 2, md: 2.2 },
          alignItems: "stretch",
        }}
      >
        {paginatedProducts.map((product) => (
          <Box
            key={product.id}
            sx={{
              width: "100%",
              minWidth: 0,
              display: "flex",
            }}
          >
            <StoreProductCard product={product} />
          </Box>
        ))}
      </Box>

      {isMobile && totalPages > 1 && (
        <Box
          sx={{
            mt: 3,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Pagination
            count={totalPages}
            page={page}
            onChange={handleChangePage}
            shape="rounded"
            sx={{
              "& .MuiPaginationItem-root": {
                fontWeight: 700,
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
}